import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { AuthContext } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

// Icons from Lucide
import { ChevronLeft, ChevronRight } from "lucide-react";

// Icons from React Icons
import { 
  HiDownload, 
  HiUser, 
  HiChatAlt2,
  HiCreditCard
} from 'react-icons/hi';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaPlay } from 'react-icons/fa';

const trackGAEvent = (category, action, label) => {
  ReactGA.event({
    category: category,
    action: action,
    label: label, 
  });
};

const BlogDetails = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  
  // Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Comment State
  const [newComment, setNewComment] = useState('');
  const [username, setUsername] = useState('');
  const [contact, setContact] = useState('');

  const fetchBlogDetails = async () => {
    try {
      const { data } = await axiosInstance.get(`/blogs/${id}`);
      setBlog(data);
    } catch (error) {
      console.error('Error fetching blog details:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBlogDetails();
      axiosInstance.put(`/blogs/${id}/click`).catch(err => console.error("Failed to increment view count.", err));
    }
  }, [id]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !username.trim()) return;

    trackGAEvent('Engagement', 'Submit Comment', blog.title);

    try {
      await axiosInstance.post(`/blogs/${id}/comment`, {
        comment: newComment,
        username: username,
        contact: contact,
      });

      fetchBlogDetails(); 
      setNewComment('');
      setUsername('');
      setContact('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  if (!blog) {
    return <div className="text-center p-10">Loading product details...</div>;
  }
  
  // --- MEDIA SLIDER LOGIC ---
  const mediaList = [];

  // 1. Add Video if available (Will be at Index 0)
  if (+blog.isVideo === 1 && blog.video_link) {
    mediaList.push({ type: "video", src: blog.video_link });
  }

  // 2. Add Images (Array or Single)
  if (Array.isArray(blog.images) && blog.images.length > 0) {
    blog.images.forEach((img) => mediaList.push({ type: "image", src: img }));
  } else if (blog.image) {
    mediaList.push({ type: "image", src: blog.image });
  }

  // Navigation Handlers
  const nextSlide = (e) => {
    if(e) e.preventDefault();
    setCurrentSlide((prev) => (prev === mediaList.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    if(e) e.preventDefault();
    setCurrentSlide((prev) => (prev === 0 ? mediaList.length - 1 : prev - 1));
  };

  // SEO & Schema
  const plainTextDescription = blog.content.replace(/<[^>]*>?/gm, '').substring(0, 160);
  const currentUrl = window.location.href;
  const keywords = `${blog.title.split(' ').join(', ')}, ${blog.category_name}`;

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": blog.title,
    "description": plainTextDescription,
    "image": blog.image || "",
    "offers": {
        "@type": "Offer",
        "price": blog.price || "0",
        "priceCurrency": "INR"
    }
  };

  return (
    <>
      <Helmet>
        <title>{blog.title}</title>
        <meta name="description" content={plainTextDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={currentUrl} />
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      </Helmet>

      <div className="bg-gray-100 min-h-screen py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          
          {/* Main Card */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            
            <div className="md:flex">
                {/* --- LEFT SIDE: MEDIA DISPLAY --- */}
                <div className="md:w-1/2 bg-gray-50 flex flex-col">
                      
                      {/* 1. MAIN VIEWER (Slider) */}
                      <div className="relative group w-full aspect-[4/5] md:aspect-square overflow-hidden bg-black/5">
                        
                        {/* Selling Fast Badge */}
                        <span className="absolute top-4 left-4 bg-[#551756] text-white text-xs uppercase font-bold px-3 py-1 z-20 shadow-md">
                            Selling Fast
                        </span>

                        {/* Media Renderer */}
                        <div className="w-full h-full relative">
                            {mediaList.map((media, index) => (
                                <div
                                key={index}
                                className={`absolute inset-0 w-full h-full transition-opacity duration-500 ease-in-out ${
                                    index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                                }`}
                                >
                                {media.type === "video" ? (
                                    <video
                                    src={media.src}
                                    className="w-full h-full object-cover"
                                    controls 
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    />
                                ) : (
                                    <img
                                    src={media.src}
                                    alt={blog.title}
                                    className="w-full h-full object-cover"
                                    />
                                )}
                                </div>
                            ))}
                        </div>

                        {/* Arrows (Only if multiple items) */}
                        {mediaList.length > 1 && (
                        <>
                            <button
                            onClick={prevSlide}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full text-[#551756] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#CAB273]"
                            >
                            <ChevronLeft size={24} />
                            </button>
                            <button
                            onClick={nextSlide}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full text-[#551756] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#CAB273]"
                            >
                            <ChevronRight size={24} />
                            </button>
                        </>
                        )}
                    </div>

                    {/* 2. THUMBNAIL SELECTOR (Horizontal List) */}
                    {mediaList.length > 1 && (
                        <div className="p-4 flex gap-3 overflow-x-auto scrollbar-hide">
                            {mediaList.map((media, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                                        currentSlide === index 
                                        ? 'border-[#551756] ring-1 ring-[#551756] opacity-100' 
                                        : 'border-transparent opacity-60 hover:opacity-100'
                                    }`}
                                >
                                    {media.type === 'video' ? (
                                        <div className="w-full h-full bg-black relative flex items-center justify-center">
                                            <video 
                                                src={media.src} 
                                                className="w-full h-full object-cover opacity-80" 
                                                muted 
                                                playsInline
                                            />
                                            {/* Play Icon Overlay for Thumbnail */}
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="bg-white/20 p-1.5 rounded-full backdrop-blur-sm">
                                                    <FaPlay className="text-white text-xs" />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <img 
                                            src={media.src} 
                                            alt={`Thumbnail ${index + 1}`} 
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* --- RIGHT SIDE: DETAILS --- */}
                <div className="md:w-1/2 p-6 md:p-10 flex flex-col">
                    <div className="mb-6">
                        <h1 className="text-3xl md:text-4xl font-serif text-[#551756] leading-tight mb-2">
                            {blog.title}
                        </h1>
                        <p className="text-2xl text-[#551756]/80 font-medium">
                            {blog.price ? `₹${blog.price}` : 'Free'}
                        </p>
                    </div>

                    {/* PDF Download */}
                    {blog.pdf_drive_link && (
                    <a 
                        href={blog.pdf_drive_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition mb-4 w-full"
                        onClick={() => trackGAEvent('Download', 'PDF Download Click', blog.title)}
                    >
                        <HiDownload size={20} /> Download PDF Info
                    </a>
                    )}
                    
                    {/* Payment Link */}
                    {blog.pay_link && (
                        <a 
                            href={blog.pay_link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center justify-center gap-3 bg-[#CAB273] text-[#551756] font-bold py-4 px-6 rounded-lg hover:bg-[#b89f60] transition shadow-md w-full uppercase tracking-widest text-sm"
                            onClick={() => trackGAEvent('Engagement', 'Payment Link Click', blog.title)}
                        >
                            <HiCreditCard size={20}/>
                            Buy Now
                        </a>
                    )}

                    {/* Form Link */}
                    {blog.form_link && (
                        <a 
                            href={blog.form_link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center justify-center gap-3 bg-purple-600 text-white font-bold py-4 px-6 rounded-lg hover:bg-purple-700 transition shadow-md w-full mt-4 uppercase tracking-widest text-sm"
                            onClick={() => trackGAEvent('Engagement', 'Form Link Click', blog.form_name)}
                        >
                            {blog.form_name || "Fill Form"}
                        </a>
                    )}

                    {/* Social Share */}
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <p className="text-sm font-semibold text-gray-500 mb-3">Share this product:</p>
                        <div className="flex gap-4">
                            <a onClick={() => trackGAEvent('Social', 'Share', 'Facebook')} href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-700 transition"><FaFacebook size={20}/></a>
                            <a onClick={() => trackGAEvent('Social', 'Share', 'Twitter')} href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${encodeURIComponent(blog.title)}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-500 transition"><FaTwitter size={20}/></a>
                            <a onClick={() => trackGAEvent('Social', 'Share', 'LinkedIn')} href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-800 transition"><FaLinkedin size={20}/></a>
                            <a onClick={() => trackGAEvent('Social', 'Share', 'WhatsApp')} href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.title + ' ' + currentUrl)}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-500 transition"><FaWhatsapp size={20}/></a>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- BOTTOM SECTION: DESCRIPTION --- */}
            <div className="p-6 md:p-10 border-t border-gray-100">
                <h3 className="text-xl font-bold text-[#551756] mb-4">Product Description</h3>
                <div className="prose lg:prose-lg max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>

          </div>

          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 mt-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-gray-800">
              <HiChatAlt2 size={28}/> Reviews & Comments ({blog.comments?.length || 0})
            </h2>
            <div className="space-y-6">
              {blog.comments && blog.comments.length > 0 ? (
                blog.comments.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-4">
                    <div className="bg-gray-100 p-3 rounded-full flex-shrink-0">
                      <HiUser className="text-gray-400"/>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{comment.username}</p>
                      <p className="text-gray-600 mt-1">{comment.comment}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No reviews yet. Be the first to review!</p>
              )}
            </div>
            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="mt-8 space-y-4">
              <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#551756] focus:border-transparent focus:outline-none" placeholder="Write your review..." required></textarea>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#551756] focus:border-transparent focus:outline-none" placeholder="Your Name" required />
                  <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#551756] focus:border-transparent focus:outline-none" placeholder="Contact (Optional)" />
              </div>
              <button type="submit" className="bg-[#551756] hover:bg-[#3f1140] text-white font-bold py-3 px-6 rounded transition uppercase tracking-wide text-sm">
                Submit Review
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;